'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { CallingIcon, EmailIcon, MessagesIcon, SocialBlockFacebookIcon, WebsiteIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { List } from 'foundations-components/transitional/components/List';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { getEventSelectorId } from '../../../../utils/analytics';
import { getFormattedUrl } from '../../../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_PATH = 'appMarketplace.detail.overview.sidebar.contact';
const Link = ({
  children,
  href,
  dataAnalyticsId,
  onClick
}) => /*#__PURE__*/_jsx(Button, {
  use: "link",
  "data-hs-analytics-id": dataAnalyticsId,
  external: true,
  href: href,
  hideExternalLinkIcon: true,
  onClick: onClick,
  children: children
});
const Contact = ({
  chatUrl,
  supportUrl,
  email,
  facebookUrl,
  hasZorseChatScope,
  hasZorseWebticketScope,
  homemade,
  isCustomer,
  onTrackInteraction,
  openHelpWidget,
  phone
}) => {
  const trackInteraction = useCallback(action => onTrackInteraction(action, {
    section: 'contact'
  }), [onTrackInteraction]);
  const renderedEmailSupportLink = useMemo(() => {
    const emailSupportIconText = /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [/*#__PURE__*/_jsx(EmailIcon, {
        color: "do-not-use-eerie"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.emailSupportLinkText`
      })]
    });
    if (homemade) {
      if (isCustomer && hasZorseWebticketScope) {
        return /*#__PURE__*/_jsx(Link, {
          dataAnalyticsId: getEventSelectorId('SIDEBAR-EMAIL-SUPPORT'),
          onClick: () => {
            trackInteraction('click copy email address');
            openHelpWidget({
              create: true
            });
          },
          children: emailSupportIconText
        });
      } else {
        return null;
      }
    }
    return /*#__PURE__*/_jsx(CopyButton, {
      "data-hs-analytics-id": getEventSelectorId('SIDEBAR-EMAIL-SUPPORT'),
      hint: unescapedText(`${I18N_PATH}.emailCopyButtonHint`),
      onClick: () => trackInteraction('click copy email address'),
      value: email,
      use: "link",
      children: emailSupportIconText
    });
  }, [email, hasZorseWebticketScope, homemade, isCustomer, openHelpWidget, trackInteraction]);
  const renderedLiveChatLink = useMemo(() => {
    const liveChatIconText = /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [/*#__PURE__*/_jsx(MessagesIcon, {
        color: "do-not-use-eerie"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.liveChatLinkText`
      })]
    });
    if (homemade) {
      if (isCustomer && hasZorseChatScope) {
        return /*#__PURE__*/_jsx(Link, {
          dataAnalyticsId: getEventSelectorId('SIDEBAR-LIVE-CHAT'),
          onClick: () => {
            trackInteraction('click live chat link');
            openHelpWidget({
              chat: true
            });
          },
          children: liveChatIconText
        });
      } else {
        return null;
      }
    }
    return chatUrl && /*#__PURE__*/_jsx(Link, {
      dataAnalyticsId: getEventSelectorId('SIDEBAR-LIVE-CHAT'),
      href: getFormattedUrl(chatUrl),
      onClick: () => trackInteraction('click live chat link'),
      children: liveChatIconText
    });
  }, [chatUrl, hasZorseChatScope, homemade, isCustomer, openHelpWidget, trackInteraction]);
  const renderedPhoneNumber = useMemo(() => {
    if (!phone) return null;
    const [phoneNumberBase, extension] = phone.split('-');
    const formattedPhoneNumberBase = /*#__PURE__*/_jsx(FormattedPhoneNumber, {
      value: phoneNumberBase
    });
    return extension ? /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      children: [/*#__PURE__*/_jsx(Link, {
        href: `tel:${phoneNumberBase}`,
        children: /*#__PURE__*/_jsx(CallingIcon, {
          color: "do-not-use-eerie"
        })
      }), /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: `${I18N_PATH}.phoneWithExtension`,
        options: {
          phoneNumberBase: /*#__PURE__*/_jsx(Link, {
            href: `tel:${phoneNumberBase}`,
            children: formattedPhoneNumberBase
          }),
          extension
        }
      })]
    }) : /*#__PURE__*/_jsx(Link, {
      href: `tel:${phoneNumberBase}`,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(CallingIcon, {
          color: "do-not-use-eerie"
        }), formattedPhoneNumberBase]
      })
    });
  }, [phone]);
  return /*#__PURE__*/_jsxs(List, {
    wrapChildren: true,
    children: [renderedEmailSupportLink, supportUrl && /*#__PURE__*/_jsx(Link, {
      dataAnalyticsId: getEventSelectorId('SIDEBAR-SUPPORT'),
      href: getFormattedUrl(supportUrl),
      onClick: () => trackInteraction('click support website link'),
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(WebsiteIcon, {
          color: "do-not-use-eerie"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.supportWebsiteLinkText`
        })]
      })
    }), renderedLiveChatLink, facebookUrl && /*#__PURE__*/_jsx(Link, {
      dataAnalyticsId: getEventSelectorId('SIDEBAR-FACEBOOK'),
      href: getFormattedUrl(facebookUrl),
      onClick: () => trackInteraction('click facebook link'),
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(SocialBlockFacebookIcon, {
          color: "do-not-use-eerie"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.facebookLinkText`
        })]
      })
    }), renderedPhoneNumber]
  });
};
Contact.propTypes = {
  chatUrl: PropTypes.string,
  supportUrl: PropTypes.string,
  email: PropTypes.string.isRequired,
  facebookUrl: PropTypes.string,
  hasZorseChatScope: PropTypes.bool.isRequired,
  hasZorseWebticketScope: PropTypes.bool.isRequired,
  homemade: PropTypes.bool.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  openHelpWidget: PropTypes.func.isRequired,
  phone: PropTypes.string
};
export default Contact;